/* eslint-disable react-hooks/rules-of-hooks */
import { useRequest } from 'ahooks';
import { Formik, FormikConfig, FormikProps } from 'formik';
import { ReactNode, useEffect, useState } from 'react';
import {
  createErpOrder,
  getErpOrder,
  getJstTraceChannel,
  getQyTraceChannel,
  reviewErpOrder,
} from '../api';
import FormField from 'components/FormField';
import { Button, Divider, Form, Input, message, Modal, ModalProps, Radio, Select } from 'antd';
import { useTranslate } from '@tolgee/react';
import { CopyOutlined } from '@ant-design/icons';
import copyText from 'utils/copyText';
import findMostSimilar from 'utils/findMostSimilar';
import { find } from 'lodash-es';
import { Flex, FV } from './share';
import ItemInfo from './ItemInfo';
import StatusButton from 'components/StatusButton';
import { extractAddressInfo } from 'services/api/conversation.convList';

interface ErpOrderFormProps extends FormikConfig<FV> {
  disabled?: boolean;
  storeId: number;
  itemOffsale?: boolean;
  wms: ERP.Wm[];
  /** 平台名称，如 Shopee，用于匹配物流渠道 */
  platform?: string;
  item: Pick<Conversation.Trade.Product, 'externalItemId' | 'itemPic' | 'itemName'>;
  skuInfo: Pick<Conversation.ProductSKUInfo, 'sku' | 'skuId' | 'sellerSku'>[];
  children: (form: ReactNode, props: FormikProps<FV>) => ReactNode;
}

function ErpOrderForm({
  disabled,
  storeId,
  itemOffsale,
  wms,
  platform,
  item,
  skuInfo,
  children,
  initialValues,
  onSubmit,
  ...otherProps
}: ErpOrderFormProps) {
  const { t } = useTranslate();

  const [newDelivery, setNewDelivery] = useState(false);

  useEffect(() => {
    const { receiverName, erpType } = initialValues;
    if (erpType === 'JST') {
      setNewDelivery(Boolean(receiverName));
    }
  }, [initialValues]);

  const [text, setText] = useState('');

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, helpers) => {
        if (!newDelivery && values.erpType === 'JST') {
          return onSubmit(
            {
              ...values,
              receiverName: '',
              phone: '',
              postCode: '',
              province: '',
              city: '',
              district: '',
              address: '',
            },
            helpers,
          );
        }
        return onSubmit(
          {
            ...values,
            skus: values.skus
              ?.filter((_) => _)
              .map(({ subSkus, skuQuantity, ...others }) => {
                return {
                  skuQuantity: subSkus.length > 0 ? 0 : skuQuantity,
                  subSkus,
                  ...others,
                };
              }),
          },
          helpers,
        );
      }}
      {...otherProps}
    >
      {(formProps) => {
        const { values, setValues } = formProps;

        const { erpType, warehouseName, traceChannel } = values;

        const qyTraceChannelState = useRequest(
          () =>
            getQyTraceChannel({
              storeId,
              warehouseName,
            }),
          {
            ready: erpType === 'QY' && Boolean(warehouseName),
            refreshDeps: [warehouseName],
            onSuccess: (data) => {
              // 发货仓变更后，自动选中与该仓名称最相似的物流渠道
              // 审核/查看态只展示补发单原有的渠道，不做自动选择
              const matched = disabled
                ? undefined
                : findMostSimilar(warehouseName, data, (_) => _.name, platform);
              if (matched) {
                setValues({
                  ...values,
                  traceChannel: matched,
                });
              } else if (data.every((_) => _.code !== traceChannel?.code)) {
                setValues({
                  ...values,
                  traceChannel: undefined,
                });
              }
            },
          },
        );
        const jstTraceChannelState = useRequest(() => getJstTraceChannel({ storeId }), {
          ready: erpType === 'JST',
          onSuccess: (data) => {
            if (
              data.every(({ channel, channelType }) => {
                return (
                  channelType !== values.channelType ||
                  channel.every(({ code }) => code !== traceChannel?.code)
                );
              })
            ) {
              setValues({
                ...values,
                channelType: undefined,
                traceChannel: undefined,
              });
            }
          },
        });
        const jstChannels = jstTraceChannelState.data?.find(
          ({ channelType }) => channelType === values.channelType,
        )?.channel;

        const form = (
          <Form layout="vertical" colon={false}>
            <ItemInfo
              disabled={disabled}
              storeId={storeId}
              itemOffsale={itemOffsale}
              item={item}
              skuInfo={skuInfo}
            />
            <FormField<FV['warehouseCode']>
              name="warehouseCode"
              required
              label={t('scene.chatWorkspace.taskTab.erpOrderForm.label.shipFromWarehouse')}
              validate={(value) =>
                value
                  ? undefined
                  : t('common.validation.requiredSelection', {
                      fieldName: t(
                        'scene.chatWorkspace.taskTab.erpOrderForm.label.shipFromWarehouse',
                      ),
                    })
              }
            >
              {({ field: { value, onBlur } }) => {
                return (
                  <Flex items={[1, 1]}>
                    <Select
                      disabled={disabled}
                      placeholder={t('common.validation.requiredSelection', {
                        fieldName: t(
                          'scene.chatWorkspace.taskTab.erpOrderForm.label.shipFromWarehouse',
                        ),
                      })}
                      value={value}
                      onChange={(code) => {
                        const target = wms.find(
                          (_) => (erpType === 'QY' ? _.name : `${_.warehouseId}`) === code,
                        );
                        if (target) {
                          setValues({
                            ...values,
                            warehouseCode: code,
                            warehouseName: target.name,
                          });
                        }
                      }}
                      onBlur={onBlur}
                      options={wms.map(({ warehouseId, name }) => ({
                        value: erpType === 'QY' ? name : `${warehouseId}`,
                        label: name,
                      }))}
                    />
                    <span />
                  </Flex>
                );
              }}
            </FormField>
            <Flex items={[1, 1]}>
              <FormField<FV['traceChannel']>
                name="traceChannel"
                required={erpType === 'QY'}
                label={
                  erpType === 'QY'
                    ? t('scene.chatWorkspace.taskTab.erpOrderForm.label.deliveryLogistics')
                    : t('scene.chatWorkspace.taskTab.erpOrderForm.label.shippingChannel')
                }
                description={
                  erpType === 'QY'
                    ? t('scene.chatWorkspace.taskTab.erpOrderForm.hint.offlineLogisticsRequired')
                    : undefined
                }
                validate={(value) =>
                  value || erpType !== 'QY'
                    ? undefined
                    : t('common.validation.requiredSelection', {
                        fieldName: t(
                          'scene.chatWorkspace.taskTab.erpOrderForm.label.shippingChannel',
                        ),
                      })
                }
              >
                {({ field: { value, onChange, onBlur } }) => {
                  const channels = erpType === 'JST' ? jstChannels : qyTraceChannelState.data;
                  const channelSelect = (
                    <Select
                      allowClear
                      showSearch
                      disabled={erpType === 'JST' ? disabled || !values.channelType : disabled}
                      loading={
                        erpType === 'JST'
                          ? jstTraceChannelState.loading
                          : qyTraceChannelState.loading
                      }
                      dropdownMatchSelectWidth={false}
                      placeholder={t('common.validation.requiredSelection', {
                        fieldName: t(
                          'scene.chatWorkspace.taskTab.erpOrderForm.label.shippingChannel',
                        ),
                      })}
                      value={value?.code}
                      onChange={(value) => {
                        if (!value) {
                          onChange(undefined);
                        } else {
                          const target = channels?.find((_) => _.code === value);
                          if (target) {
                            onChange(target);
                          }
                        }
                      }}
                      onBlur={onBlur}
                      options={channels?.map(({ code, name }) => ({
                        value: code,
                        label: name,
                      }))}
                      optionFilterProp="label"
                    />
                  );
                  if (erpType !== 'JST') {
                    return channelSelect;
                  }
                  const jstChannelSelect = (
                    <Select
                      style={{ marginBottom: 16 }}
                      allowClear
                      showSearch
                      disabled={disabled}
                      loading={jstTraceChannelState.loading}
                      dropdownMatchSelectWidth={false}
                      placeholder={t('common.validation.requiredSelection', {
                        fieldName: t(
                          'scene.chatWorkspace.taskTab.erpOrderForm.label.shippingCarrier',
                        ),
                      })}
                      value={values.channelType}
                      onChange={(value) => {
                        setValues({
                          ...values,
                          channelType: value,
                          traceChannel: undefined,
                        });
                      }}
                      options={jstTraceChannelState.data?.map(({ name, channelType }) => ({
                        value: channelType,
                        label: name,
                      }))}
                      optionFilterProp="label"
                    />
                  );
                  return (
                    <div>
                      {jstChannelSelect}
                      {channelSelect}
                    </div>
                  );
                }}
              </FormField>
              <span />
            </Flex>
            <section
              style={{
                padding: '12px',
                background: 'var(--fett-bg-subtle)',
              }}
            >
              <h4>{t('scene.chatWorkspace.taskTab.erpOrderForm.label.shippingInfo')}</h4>
              {erpType === 'JST' && (
                <div>
                  <Radio
                    disabled={disabled}
                    checked={!newDelivery}
                    onChange={({ target: { checked } }) => checked && setNewDelivery(false)}
                  >
                    {t('scene.chatWorkspace.taskTab.erpOrderForm.action.useRelatedOrderAddress')}
                  </Radio>
                  <Radio
                    disabled={disabled}
                    checked={newDelivery}
                    onChange={({ target: { checked } }) => checked && setNewDelivery(true)}
                  >
                    {t('scene.chatWorkspace.taskTab.erpOrderForm.action.useNewAddress')}
                  </Radio>
                </div>
              )}
              {erpType === 'JST' && newDelivery && <Divider type="horizontal" />}
              {(erpType !== 'JST' || newDelivery) && (
                <>
                  {!disabled && (
                    <div className="mb-4">
                      <Input.TextArea
                        value={text}
                        onChange={({ target: { value } }) => setText(value)}
                        placeholder="粘贴文本到此处"
                      />
                      <StatusButton
                        className="!pl-0"
                        type="link"
                        onClick={() => {
                          const extract = (text: string) => {
                            return extractAddressInfo({ text })
                              .then((res) => {
                                const { name, mobile, zipcode, state, city, district, address } =
                                  res;

                                setValues({
                                  receiverName: name,
                                  phone: mobile,
                                  postCode: erpType === 'QY' ? zipcode : values.postCode,
                                  province: state,
                                  city,
                                  district,
                                  address,
                                });
                              })
                              .catch(() => {
                                message.error('识别失败，请稍后再试');
                              });
                          };
                          if (text) {
                            return extract(text);
                          }
                          return navigator.clipboard.readText().then((text) => {
                            if (text) {
                              setText(text);
                              return extract(text);
                            }
                            message.warn('未读取到剪切板信息');
                          });
                        }}
                      >
                        {text ? '识别' : '粘贴并识别'}
                      </StatusButton>
                    </div>
                  )}
                  <Flex items={[1, 1, 1]}>
                    <FormField<FV['receiverName']>
                      name="receiverName"
                      required
                      label={t('scene.chatWorkspace.taskTab.erpOrderForm.label.recipientName')}
                      validate={(value) =>
                        value
                          ? undefined
                          : t('common.validation.requiredInput', {
                              fieldName: t(
                                'scene.chatWorkspace.taskTab.erpOrderForm.label.recipientName',
                              ),
                            })
                      }
                    >
                      {({ field }) => {
                        return (
                          <Input
                            showCount
                            disabled={disabled}
                            maxLength={40}
                            placeholder={t(
                              'scene.chatWorkspace.taskTab.erpOrderForm.label.recipientName',
                            )}
                            {...field}
                          />
                        );
                      }}
                    </FormField>
                    <FormField<FV['phone']>
                      name="phone"
                      required
                      label={t('scene.chatWorkspace.taskTab.erpOrderForm.label.phoneNumber')}
                      validate={(value) =>
                        value
                          ? undefined
                          : t('common.validation.requiredInput', {
                              fieldName: t(
                                'scene.chatWorkspace.taskTab.erpOrderForm.label.phoneNumber',
                              ),
                            })
                      }
                    >
                      {({ field }) => {
                        return (
                          <Input
                            disabled={disabled}
                            placeholder={t(
                              'scene.chatWorkspace.taskTab.erpOrderForm.label.phoneNumber',
                            )}
                            {...field}
                          />
                        );
                      }}
                    </FormField>
                    {erpType === 'QY' ? (
                      <FormField<FV['postCode']>
                        name="postCode"
                        required
                        label={t('domain.order.field.postalCode')}
                        validate={(value) =>
                          value
                            ? undefined
                            : t('common.validation.requiredInput', {
                                fieldName: t('domain.order.field.postalCode'),
                              })
                        }
                      >
                        {({ field }) => {
                          return (
                            <Input
                              disabled={disabled}
                              placeholder={t('domain.order.field.postalCode')}
                              {...field}
                            />
                          );
                        }}
                      </FormField>
                    ) : (
                      <div />
                    )}
                  </Flex>
                  <Flex items={[1, 1, 1]}>
                    <FormField<FV['province']>
                      name="province"
                      required
                      label={t('scene.chatWorkspace.taskTab.erpOrderForm.label.addressInfo')}
                      validate={(value) =>
                        value
                          ? undefined
                          : t('common.validation.requiredInput', {
                              fieldName: t(
                                'scene.chatWorkspace.taskTab.erpOrderForm.label.provinceState',
                              ),
                            })
                      }
                    >
                      {({ field }) => {
                        return (
                          <Input
                            disabled={disabled}
                            placeholder={t(
                              'scene.chatWorkspace.taskTab.erpOrderForm.label.provinceState',
                            )}
                            {...field}
                          />
                        );
                      }}
                    </FormField>
                    <FormField<FV['city']>
                      name="city"
                      label=" "
                      validate={(value) =>
                        value
                          ? undefined
                          : t('common.validation.requiredInput', {
                              fieldName: t('scene.chatWorkspace.taskTab.erpOrderForm.label.city'),
                            })
                      }
                    >
                      {({ field }) => {
                        return (
                          <Input
                            disabled={disabled}
                            placeholder={t('scene.chatWorkspace.taskTab.erpOrderForm.label.city')}
                            {...field}
                          />
                        );
                      }}
                    </FormField>
                    <FormField<FV['district']>
                      name="district"
                      label=" "
                      validate={(value) =>
                        value
                          ? undefined
                          : t('common.validation.requiredInput', {
                              fieldName: t(
                                'scene.chatWorkspace.taskTab.erpOrderForm.label.districtCounty',
                              ),
                            })
                      }
                    >
                      {({ field }) => {
                        return (
                          <Input
                            disabled={disabled}
                            placeholder={t(
                              'scene.chatWorkspace.taskTab.erpOrderForm.label.districtCounty',
                            )}
                            {...field}
                          />
                        );
                      }}
                    </FormField>
                  </Flex>
                  <FormField<FV['address']>
                    name="address"
                    required
                    label={t('scene.chatWorkspace.taskTab.erpOrderForm.label.detailedAddress')}
                    validate={(value) =>
                      value
                        ? undefined
                        : t('common.validation.requiredInput', {
                            fieldName: t(
                              'scene.chatWorkspace.taskTab.erpOrderForm.label.detailedAddress',
                            ),
                          })
                    }
                  >
                    {({ field }) => {
                      return (
                        <Input.TextArea
                          disabled={disabled}
                          placeholder={t('common.validation.requiredInput', {
                            fieldName: t(
                              'scene.chatWorkspace.taskTab.erpOrderForm.label.detailedAddress',
                            ),
                          })}
                          {...field}
                        />
                      );
                    }}
                  </FormField>
                </>
              )}
            </section>
          </Form>
        );

        return children(form, {
          ...formProps,
          handleSubmit: (e) => {
            if (
              values.skus
                ?.filter((_) => _)
                .some(({ skuId }, index) => {
                  return values.skus?.slice(0, index).some((_) => _.skuId === skuId);
                })
            ) {
              return message.error(
                t('scene.chatWorkspace.taskTab.erpOrderForm.validation.productCodeDuplicate'),
              );
            }
            formProps.validateForm().then((errors) => {
              if (errors.skus?.length) {
                const target = (errors.skus as any as string[]).find((_) => typeof _ === 'string');
                if (target) {
                  message.error(target);
                }
              }
            });
            formProps.handleSubmit(e);
          },
        });
      }}
    </Formik>
  );
}

export type ErpOrderModalProps = ModalProps &
  Pick<
    ErpOrderFormProps,
    'storeId' | 'itemOffsale' | 'wms' | 'platform' | 'skuInfo' | 'initialValues'
  > & {
    mode: 'create' | 'review' | 'view' | 'createReview';
    item: Pick<Conversation.Trade.Product, 'externalItemId' | 'itemPic' | 'itemName'>;
    onUpdate: () => void;
    source?: string; // 用于区分创建补发单的来源
  };

export function ErpOrderModal({
  mode,
  item,
  storeId,
  itemOffsale,
  wms,
  platform,
  skuInfo,
  initialValues,
  onUpdate,
  source,
  ...otherProps
}: ErpOrderModalProps) {
  const { t } = useTranslate();

  return (
    <ErpOrderForm
      disabled={mode === 'review' || mode === 'view'}
      storeId={storeId}
      itemOffsale={itemOffsale}
      wms={wms}
      platform={platform}
      item={item}
      skuInfo={skuInfo}
      initialValues={initialValues}
      onSubmit={(values) => {
        return (
          mode === 'create'
            ? createErpOrder({ erpOrder: { ...values, source } })
            : mode === 'createReview'
            ? createErpOrder({ erpOrder: { ...values, source } }).then(() => {
                return getErpOrder(values.todoId || '')
                  .then((res) => {
                    const targetOrder = find(res.orders, (o) => o.skuId === values.skuId);
                    if (!targetOrder) {
                      return Promise.reject(new Error('no erp order'));
                    }
                    const { todoId, warehouseCode, warehouseName, erpId } = targetOrder;
                    return reviewErpOrder({ todoId, warehouseCode, warehouseName, erpId });
                  })
                  .catch((err) => {
                    onUpdate();
                    return Promise.reject(err);
                  });
              })
            : mode === 'review'
            ? reviewErpOrder({
                erpId: values?.erpId || '',
                todoId: values.todoId || '',
                warehouseName: values.warehouseName || '',
                warehouseCode: values.warehouseCode || '',
              })
            : Promise.reject(new Error('invalid mode'))
        )
          .then(() => {
            message.success(t('scene.chatWorkspace.taskTab.erpOrderForm.feedback.saveSuccess'));
            onUpdate();
          })
          .catch((e) => {
            message.error(
              e?.response?.data?.msg ||
                t('scene.chatWorkspace.taskTab.erpOrderForm.feedback.saveFailed'),
            );
          });
      }}
    >
      {(form, { values: { erpTradeId }, isSubmitting, handleSubmit }) => {
        return (
          <Modal
            width={680}
            title={
              {
                create: t('scene.chatWorkspace.taskTab.erpOrder.action.createReshipment'),
                review: t('scene.chatWorkspace.taskTab.erpOrder.action.auditReshipment'),
                view: t('scene.chatWorkspace.taskTab.erpOrder.action.viewReshipment'),
                createReview: t('scene.chatWorkspace.taskTab.erpOrder.action.createReshipment'),
              }[mode]
            }
            footer={
              mode === 'view' ? (
                false
              ) : (
                <div className="flex items-center justify-end">
                  {mode === 'review' && (
                    <div className="mr-4">
                      {t('scene.chatWorkspace.taskTab.erpOrder.label.reshipmentOrderNo')}：
                      {erpTradeId}
                      <CopyOutlined
                        onClick={() => {
                          copyText(erpTradeId || '').then(() => {
                            message.success(t('common.feedback.copySuccess'));
                          });
                        }}
                      />
                    </div>
                  )}
                  <Button onClick={otherProps.onCancel}>{t('common.action.cancel')}</Button>
                  <Button type="primary" loading={isSubmitting} onClick={() => handleSubmit()}>
                    {mode === 'create' && t('common.action.create')}
                    {mode === 'review' &&
                      t('scene.chatWorkspace.taskTab.erpOrderForm.action.confirmAudit')}
                    {mode === 'createReview' &&
                      t('scene.chatWorkspace.taskTab.erpOrderForm.action.createAndAudit')}
                  </Button>
                </div>
              )
            }
            {...otherProps}
          >
            {form}
          </Modal>
        );
      }}
    </ErpOrderForm>
  );
}

export default ErpOrderForm;
